import { Injectable } from '@angular/core';
import { ApiEntityService } from '@core/api/api-entity.service';
import { Automapper } from '@core/api/mapper';
import { UserNamespacedResourceBaseUrlBuilder } from '@core/user-namespaced-resource-base-url-builder.service';

import { ApiService } from '@core/api/api.service';
import { Client } from '@core/models/client.model';
import { Product } from '@core/models/product.model';
import { UserDetails } from '@core/models/user-details.model';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { ClientState, ClientStore } from './client.store';
import { finalize, map, tap, share, catchError } from 'rxjs/operators';
import { SignableDoument } from '@core/models/signable-document.model';
import { ContactCopy, ContactSerializer } from '@core/models/contacts-copy.model';
import { ContactStore } from '@core/contact/contact.store';
import { ClientMetadataModel } from '@core/models/client-metadata.model';
import { extractFormValue } from '@core/utils';
import { OrganizationQuery } from '@core/organization/organization.query';

/** Invited product row from subscriptions/clients/{id}/invited-products/ */
export interface ClientInvitedProductItem {
  product: Product;
  permission_uid?: string;
  permissionUid?: string;
  access_level?: number;
  accessLevel?: number;
  client_issuing_uid?: string | null;
  clientIssuingUid?: string | null;
  issuer_uid?: string | null;
  issuerUid?: string | null;
}

@Injectable({ providedIn: 'root' })
export class ClientService extends ApiEntityService<ClientState> {
  private isLoading = new BehaviorSubject<boolean>(false);
  private pendingRequests = new Map<string, Observable<Client | Client[]>>();

  constructor(
    store: ClientStore,
    urlBuilder: UserNamespacedResourceBaseUrlBuilder,
    private apiService: ApiService,
    private contactsStore: ContactStore,
    private organizationQuery: OrganizationQuery,
  ) {
    super(store, urlBuilder, Automapper.create(Client));
    this.overrideResourceName('clients');
  }

  // Override the get method to add caching for individual client requests
  public get<T>(id?: any, config?: any): Observable<T> {
    // Only apply caching for individual client requests (when id is provided)
    if (id && typeof id === 'string') {
      return this.getClientWithCache(id) as Observable<T>;
    }

    // For collection requests (no id), check if we have cached data
    if (!id) {
      return this.getClientsWithCache() as Observable<T>;
    }

    // For other requests, use the original method
    return super.get<T>(id, config);
  }

  private getClientWithCache(id: string): Observable<Client> {
    // Check if client is already in store
    const existingClient = this.store.getValue().entities[id];
    if (existingClient) {
      return of(existingClient);
    }

    // Check if there's already a pending request for this client
    if (this.pendingRequests.has(id)) {
      return this.pendingRequests.get(id)! as Observable<Client>;
    }

    // Make new API request and cache it
    const request$ = super.get<Client>(id).pipe(
      tap((client) => {
        // Store the client and set as active
        this.store.replace(id, client);
        this.store.setActive(id);
      }),
      finalize(() => {
        // Remove from pending requests when complete
        this.pendingRequests.delete(id);
      }),
      share(), // Share the observable to prevent multiple HTTP calls
    );

    // Cache the pending request
    this.pendingRequests.set(id, request$);
    return request$;
  }

  private getClientsWithCache(): Observable<Client[]> {
    const cacheKey = 'clients_list';

    // Check if there's already a pending request for the clients list
    if (this.pendingRequests.has(cacheKey)) {
      return this.pendingRequests.get(cacheKey)! as Observable<Client[]>;
    }

    // Make new API request and cache it
    const request$ = super.get<Client[]>().pipe(
      tap((clients) => {
        // Store all clients in the store
        clients.forEach((client) => {
          this.store.replace(client.id, client);
        });
      }),
      finalize(() => {
        // Remove from pending requests when complete
        this.pendingRequests.delete(cacheKey);
      }),
      share(), // Share the observable to prevent multiple HTTP calls
    );

    // Cache the pending request
    this.pendingRequests.set(cacheKey, request$);
    return request$;
  }

  /**
   * Forces a fresh fetch of client data, bypassing cache
   * Useful for onboarding flows where we need the latest classification data
   */
  public getClientFresh(id: string): Observable<Client> {
    // Remove from cache to force fresh fetch
    this.pendingRequests.delete(id);

    const request$ = super.get<Client>(id).pipe(
      tap((client) => {
        // Store the fresh client data and set as active
        this.store.replace(id, client);
        this.store.setActive(id);
      }),
      share(),
    );

    return request$;
  }

  /**
   * Resolve a client reference (numeric PK or UUID) to the numeric Client PK.
   * Product opening Keep Private requires the PK (DRF FK), while routes use UUID.
   */
  public resolveClientPk(clientRef: string | number | null | undefined): Observable<number | null> {
    if (clientRef == null || clientRef === '') {
      return of(null);
    }

    if (typeof clientRef === 'number' && Number.isFinite(clientRef)) {
      return of(clientRef);
    }

    const raw = String(clientRef).trim();
    if (!raw) {
      return of(null);
    }

    if (/^\d+$/.test(raw)) {
      return of(parseInt(raw, 10));
    }

    const cached = this.store.getValue().entities?.[raw] as Client | undefined;
    if (cached?.pk != null && Number.isFinite(Number(cached.pk))) {
      return of(Number(cached.pk));
    }

    return this.getClientWithCache(raw).pipe(
      map((client) => {
        const pk = client?.pk;
        return pk != null && Number.isFinite(Number(pk)) ? Number(pk) : null;
      }),
      catchError(() => of(null)),
    );
  }

  /**
   * Refreshes client classification after non-advised or advised updates
   * This ensures the client store has the latest classification data
   */
  public refreshClientClassification(clientId: string): Observable<Client> {
    return this.getClientFresh(clientId).pipe(
      tap((client) => {
        // Normalize clientClass using the same logic as business type updates
        const validClientClasses = [
          'eligible-counterparty',
          'professional-investors',
          'elective-professionals',
          'semi-professionals',
          'retail-investor',
        ];

        const clientClass = client?.clientClass;
        const normalizedClientClass =
          clientClass &&
          typeof clientClass === 'string' &&
          clientClass.trim() !== '' &&
          validClientClasses.includes(clientClass)
            ? clientClass
            : null;

        // Update store with normalized classification
        this.store.updateActive({
          clientClass: normalizedClientClass,
        });
      }),
    );
  }

  public getSelectedClientsIds(): string[] {
    return this.store.getValue().selectedIds ?? [];
  }

  public updateSelectedClientsIds(ids: string[]): void {
    this.store.update({ selectedIds: ids });
  }

  public assignClientToUserAdvisor(userDetailsId: UserDetails['id'], clientId: string): Observable<unknown> {
    const formData: FormData = new FormData();
    formData.append('client', clientId);
    formData.append('advisor_user', userDetailsId);
    const baseUrl = this.urlBuilder.buildBaseUrl();
    return this.apiService.patch(`${baseUrl}/clients/${clientId}/update`, formData);
  }

  public getClientMarketPlaceProducts(id): Observable<Product[]> {
    const clientId = id;
    const baseUrl = this.buildInvestorMarketplaceBaseUrl();

    this.isLoading.next(true);

    if (!baseUrl) {
      return of([]).pipe(finalize(() => this.isLoading.next(false)));
    }

    return this.apiService
      .get<Product[]>(`${baseUrl}/clients/${clientId}/products`)
      .pipe(finalize(() => this.isLoading.next(false)));
  }

  /**
   * Marketplace products always live under the active LP (`investors/{id}`), never under
   * `emittents/{issuerUid}` even when fund-ddq-runs context overrides other API builders.
   */
  private buildInvestorMarketplaceBaseUrl(): string | null {
    const organization = this.organizationQuery.getActive();
    if (!organization?.id || organization.type !== 'investor') {
      return null;
    }
    return `investors/${organization.id}`;
  }

  public getClientInvitedProducts(id: string): Observable<Product[]> {
    const clientId = id;
    this.isLoading.next(true);

    return this.apiService
      .get<{ results?: ClientInvitedProductItem[] } | ClientInvitedProductItem[]>(
        `subscriptions/clients/${clientId}/invited-products/`,
      )
      .pipe(
        map((response) => {
          const items = Array.isArray(response) ? response : response?.results || [];
          return items
            .map((item) => this.mapInvitedProductItem(item))
            .filter((product): product is Product => Boolean(product));
        }),
        finalize(() => this.isLoading.next(false)),
      );
  }

  private mapInvitedProductItem(item: ClientInvitedProductItem): Product | null {
    if (!item?.product) {
      return null;
    }
    const product = item.product as Product & Record<string, unknown>;
    const clientIssuingUid = item.client_issuing_uid ?? item.clientIssuingUid ?? null;
    const issuerUid = item.issuer_uid ?? item.issuerUid ?? null;
    if (clientIssuingUid) {
      product.clientIssuingUid = clientIssuingUid;
      product.issuingId = clientIssuingUid;
    }
    if (issuerUid) {
      product.issuerUid = issuerUid;
    }
    return product;
  }

  public resolveClientIssuingId(
    clientId: string,
    productId: string,
    isClientUserContext: boolean = false,
  ): Observable<string | null> {
    if (!clientId || !productId) {
      return of(null);
    }
    const endpoint = this.buildClientIssuingsListEndpoint(clientId, isClientUserContext);
    if (!endpoint) {
      return of(null);
    }

    return this.apiService
      .get<Array<{ uid?: string; product?: { uid?: string; id?: string } }> | { results?: Array<{ uid?: string; product?: { uid?: string; id?: string } }> }>(
        endpoint,
      )
      .pipe(
        map((response) => {
          const items = Array.isArray(response) ? response : response?.results || [];
          const match = items.find((item) => {
            const itemProductId =
              item?.product?.uid || item?.product?.id || (item as any)?.productUid || (item as any)?.product_uid;
            return itemProductId === productId;
          });
          return match?.uid || (match as any)?.id || null;
        }),
        catchError(() => of(null)),
      );
  }

  /**
   * Resolves the API path for listing client issuings (used to map product → issuing uid).
   */
  private buildClientIssuingsListEndpoint(clientId: string, isClientUserContext: boolean): string | null {
    if (isClientUserContext) {
      return `accounts/client/${clientId}/client-issuings`;
    }

    const investorId = this.resolveInvestorIdForClientIssuings();
    if (investorId) {
      return `investors/${investorId}/clients/${clientId}/client-issuings`;
    }

    const baseUrl = this.urlBuilder.buildBaseUrl();
    if (!baseUrl) {
      return null;
    }
    return `${baseUrl}/clients/${clientId}/client-issuings`;
  }

  private resolveInvestorIdForClientIssuings(): string | null {
    const activeOrganization = this.organizationQuery.getActive?.() as { type?: string; id?: string } | null;
    if (activeOrganization?.type === 'investor' && activeOrganization?.id) {
      return activeOrganization.id;
    }

    const namespace = this.urlBuilder.buildBaseUrl();
    const match = namespace?.match(/^investors\/([^/]+)/);
    return match?.[1] ?? null;
  }

  public resolveEmittentUidForProduct(productId: string, productName?: string): Observable<string | null> {
    if (!productId || !productName?.trim()) {
      return of(null);
    }
    const endpoint = `subscriptions/funds/search/?query=${encodeURIComponent(productName.trim())}`;
    return this.apiService.get<{ products?: Array<{ product_uid?: string; productUid?: string; issuer_uid?: string; issuerUid?: string }> }>(endpoint).pipe(
      map((response) => {
        const products = response?.products || [];
        const match = products.find((item) => (item.product_uid || item.productUid) === productId);
        return (match?.issuer_uid || match?.issuerUid || null) as string | null;
      }),
      catchError(() => of(null)),
    );
  }

  fetchAllContacts(id) {
    const baseUrl = this.urlBuilder.buildBaseUrl();
    const clientId = id;
    return this.apiService.get<ContactCopy[]>(`${baseUrl}/clients/${clientId}/contacts`).pipe(
      map((contacts) =>
        contacts.map((c) => {
          const deserializedContact = new ContactSerializer().deserialize({ ...(c as any) });
          if (deserializedContact.signedDeclaration) {
            deserializedContact.signedDeclaration.status = 'uploaded';
          }
          if (deserializedContact.idCardScan) {
            deserializedContact.idCardScan.status = 'uploaded';
          }
          return deserializedContact;
        }),
      ),
      tap((contacts) => {
        this.contactsStore.set([...contacts]);
      }),
    );
  }

  fetchClientMetaData(clientId: string): Observable<ClientMetadataModel> {
    const baseUrl = this.urlBuilder.buildBaseUrl();
    return this.apiService.get<ClientMetadataModel>(`${baseUrl}/clients/${clientId}/meta`);
  }

  addSingleContact(id, data) {
    const baseUrl = this.urlBuilder.buildBaseUrl();
    const clientId = id;
    let contactData = data;
    const formData = new FormData();
    Object.entries(contactData).forEach(([key, value]) => {
      formData.append(key, extractFormValue(value));
    });

    return this.apiService.post<ContactCopy[]>(`${baseUrl}/clients/${clientId}/contacts`, formData).pipe(
      tap((newContact) => {
        const deserializedContact = new ContactSerializer().deserialize(newContact as any);
        this.contactsStore.add(deserializedContact);
      }),
    );
  }

  deleteSingleContact(id, contractId) {
    const baseUrl = this.urlBuilder.buildBaseUrl();
    const clientId = id;
    const contactId = contractId;

    return this.apiService
      .delete(`${baseUrl}/clients/${clientId}/contacts/${contactId}`)
      .pipe(tap(() => this.contactsStore.remove(contactId)));
  }

  updateSingleContact(id, newcontactData) {
    const formData = new FormData();
    const baseUrl = this.urlBuilder.buildBaseUrl();
    const clientId = id;
    const contactData = newcontactData;
    Object.entries(contactData).forEach(([key, value]) => {
      formData.append(key, extractFormValue(value));
    });
    return this.apiService.patch(`${baseUrl}/clients/${clientId}/contacts/${contactData.uid}`, formData).pipe(
      tap((updatedContact: ContactCopy) => {
        const deserializedContact = new ContactSerializer().deserialize(updatedContact as any);
        this.contactsStore.update((state) => {
          return {
            ...state,
            entities: {
              ...state.entities,
              [deserializedContact.uid]: deserializedContact,
            },
          };
        });
      }),
    );
  }

  public isLoading$(): Observable<boolean> {
    return this.isLoading.asObservable();
  }

  public updateBusinessType(client: Client) {
    const baseUrl = this.urlBuilder.buildBaseUrl();

    const formData = new FormData();
    formData.append('business_type', client.businessType);
    return this.apiService.patch<Client>(`${baseUrl}/clients/${client.id}/update`, formData).pipe(
      tap((updatedClient) => {
        // Define valid client classifications
        const validClientClasses = [
          'eligible-counterparty',
          'professional-investors',
          'elective-professionals',
          'semi-professionals',
          'retail-investor',
        ];

        // Extract clientClass safely
        const clientClass = updatedClient?.clientClass
          ? (updatedClient?.clientClass as unknown as { codename: string })?.codename
          : null;

        // Normalize clientClass: null for invalid values, keep valid ones
        const normalizedClientClass =
          clientClass &&
          typeof clientClass === 'string' &&
          clientClass.trim() !== '' &&
          validClientClasses.includes(clientClass)
            ? clientClass
            : null;

        this.store.updateActive({
          ...updatedClient,
          businessType: updatedClient?.businessType
            ? (updatedClient?.businessType as unknown as { codename: string }).codename
            : null,
          // Always update clientClass to ensure invalid values become null
          clientClass: normalizedClientClass,
        });
      }),
    );
  }

  public fetchSignDocumentData(id: string, clientId: string, authCode?: string): Observable<SignableDoument> {
    const baseUrl = this.urlBuilder.buildBaseUrl();
    const formData = new FormData();
    formData.append('basket_item', id);
    if (authCode) {
      formData.append('auth_code', authCode);
    }
    return this.apiService.post<SignableDoument>(`${baseUrl}/clients/${clientId}/envelope`, formData);
  }

  public buyFromCustodia(id: string, clientId): Observable<void> {
    const baseUrl = this.urlBuilder.buildBaseUrl();
    const formData = new FormData();
    formData.append('basket_item', id);
    return this.apiService.post<void>(`${baseUrl}/clients/${clientId}/custodian`, formData);
  }

  public deleteClient(client: Client) {
    const clientId = client?.id;
    const baseUrl = this.urlBuilder.buildBaseUrl();
    return this.apiService.delete(`${baseUrl}/clients/${clientId}/delete`).pipe(tap(() => this.store.remove(clientId)));
  }
}
